import { CombinedState, createSelector } from '@reduxjs/toolkit'
import { useFlag, useLDClient } from '@afterpaytouch/feature'
import { useEffect, useRef, useState } from 'react'
import { Cohort } from '@afterpaytouch/portal-api'
import { DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import { PaymentType } from '@afterpay/types'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import {
  CheckoutAppSelector,
  CheckoutAppState,
  CheckoutAppStore,
  ExperimentsState,
  triggerExperiment,
  useActiveCashAppPay,
  useAppDispatch,
  useAppSelector,
  useCashLoginType,
  useCohort,
  useConsumerFeatureFlagReady,
  useConsumerLendingEligible,
  useConsumerSelector,
  useIsEmptyCheckoutToken,
  useIsRecognizedViaPersistentLogin,
  useIsTrustedCheckout,
  usePreferredCard,
  useShouldSetApplePayAsPurchaseMethod,
  useShouldUseBankAccount,
} from '../'
import { Experiment, Experiments, ExperimentValue, Features, getExperiment, KnownExperiment } from '../../model'
import { useStore } from 'react-redux'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { consumeInteractiveLoginSignal, peekInteractiveLoginSignal } from '../../flow/remember-me/loginSignal'

export const experimentsSelector: CheckoutAppSelector<ExperimentsState> = (state) => state.experiments

export const activeExperiments: CheckoutAppSelector<string> = createSelector(experimentsSelector, ({ activeExperiments }) => {
  return Object.entries(activeExperiments)
    .map(([experiment, value]) => `${experiment}${value}`)
    .join(',')
})

const activeExperimentsSelector = (state: CombinedState<CheckoutAppState>): Partial<Record<KnownExperiment, ExperimentValue>> => {
  return state.experiments.activeExperiments
}

export const useExperimentsState = (): ExperimentsState => useAppSelector(experimentsSelector)

export const useActiveExperiments = (): string => useAppSelector(activeExperiments)

export const useTriggerExperimentEvent = (): ((params: Experiment, customEventProperties?: Record<string, unknown>) => void) => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()
  const store: CheckoutAppStore = useStore()
  const isEmptyCheckoutToken: boolean = useIsEmptyCheckoutToken()
  return ({ name, value, control }: Experiment, customEventProperties?: Record<string, unknown>): void => {
    const activeExperiments = activeExperimentsSelector(store.getState())
    const analyticsFired = name in activeExperiments
    if (!analyticsFired && !isEmptyCheckoutToken) {
      dispatch(triggerExperiment([name, value]))
      logEvent('Triggered Experiment', {
        experimentName: Experiments[name],
        experimentValue: value,
        experimentControl: control,
        experimentFlag: `${name}${value}`,
        ...customEventProperties,
      })
    }
  }
}

export const useExperimentTrigger = (experiment: Experiment): ((customEventProperties?: Record<string, unknown>) => void) => {
  const [fire, setFired] = useState(false)
  const [customEventProperties, setCustomEventProperties] = useState({})
  const triggerExperimentEvent = useTriggerExperimentEvent()

  useEffect(() => {
    if (experiment.active && !experiment.loading && fire) {
      triggerExperimentEvent(experiment, customEventProperties)
    }
  }, [experiment.name, experiment.active, experiment.value, fire, triggerExperimentEvent, experiment, customEventProperties])

  return (customEventProperties) => {
    setFired(true)
    if (customEventProperties !== undefined) {
      setCustomEventProperties(customEventProperties)
    }
  }
}

// delays experiment trigger until experiment is passed in
export const useDelayedExperimentTrigger = (): ((updatedExperiment: Experiment) => void) => {
  const [{ isFired, givenExperiment }, setFiredExperiment] = useState<{ isFired: boolean; givenExperiment: Experiment | undefined }>({
    isFired: false,
    givenExperiment: undefined,
  })
  const triggerExperimentEvent = useTriggerExperimentEvent()

  useEffect(() => {
    if (givenExperiment === undefined) {
      return
    }

    if (givenExperiment.active && !givenExperiment.loading && isFired) {
      triggerExperimentEvent(givenExperiment)
    }
  }, [givenExperiment, givenExperiment?.name, givenExperiment?.active, givenExperiment?.value, givenExperiment?.control, isFired, triggerExperimentEvent])

  return (experiment) =>
    setFiredExperiment({
      isFired: true,
      givenExperiment: experiment,
    })
}

export function useExperiment(name: KnownExperiment, fireEvent: boolean = true, customisedExperiment: Experiment | null = null): Experiment {
  const client = useLDClient()
  const consumerFeatureFlagReady = useConsumerFeatureFlagReady()
  const { activeExperiments } = useExperimentsState()
  let experiment = customisedExperiment
  if (experiment === null) {
    const ldValue = client?.variationDetail(Experiments[name], false)
    experiment = getExperiment(name, ldValue, activeExperiments[name])
  }

  const experimentTrigger = useExperimentTrigger(experiment)

  useEffect(() => {
    if (fireEvent && consumerFeatureFlagReady) {
      experimentTrigger()
    }
  }, [consumerFeatureFlagReady, experimentTrigger, fireEvent])

  return experiment
}

// Fires the `Triggered Experiment` exposure for the AP-cohort remember-me experiment (CASHDAP-3556)
// exactly once per fresh interactive login (password / OTP / biometric / 2FA). Mounted on the
// LoggedIn page, where every fresh login converges — deliberately NOT inside the remember-me mint
// hook (`useMintPersistentSessionOnLogin`), whose gating (including the flag check itself) excludes
// the control arm and would bias the enrolled populations; both arms pass through LoggedIn. The
// one-shot login signal (see `loginSignal.ts`) excludes silent persistent-login resume and mid-flow
// re-logins, so the exposure is anchored to the login that just happened. `useExperiment(..., false)`
// resolves the variant without auto-firing, so this hook is the sole trigger.
//
// The flag is targeted per-consumer uuid, and `useExperiment(..., false)` bypasses the readiness
// gate the auto-fire path enforces: LD bootstraps from localStorage, so before the post-login
// identify completes `variation()` can return a stale (anonymous or previous user's) value. Mirror
// the mint's "wait until the client context matches" idiom — hold the pending exposure until the LD
// client's context key is this consumer's uuid AND flags report ready — so the logged variant is
// the resolved current-user assignment.
//
// Dedup is per LOGIN GENERATION, not per experiment-session: `useTriggerExperimentEvent`'s dedup
// keys on the `experiments.activeExperiments` slice, which is never cleared on logout, so routing
// through it would suppress the exposure for a second interactive login in the same tab
// (logout → re-login). Instead we log directly and PEEK the signal while gates resolve, only
// CONSUMING it at the moment the event fires — the signal lives in module state, so a pre-fire
// unmount/detour (e.g. ADD_MOBILE) preserves the pending exposure for the next LoggedIn mount,
// while consume-at-fire still bounds it to exactly once per login. We still
// `dispatch(triggerExperiment(...))` so the `activeExperiments` slice (and the derived
// `activeExperiment` enduring property) reflects the assignment.
export const useTriggerRememberMeExperimentExposure = (): void => {
  const experiment = useExperiment('AutoRememberMe', false)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()
  const isEmptyCheckoutToken = useIsEmptyCheckoutToken()
  const { uuid, hasFeatureFlagsReady } = useConsumerSelector()
  const isRecognizedViaPersistentLogin = useIsRecognizedViaPersistentLogin()
  const isTrustedCheckout = useIsTrustedCheckout()
  const ldClient = useLDClient()

  useEffect(() => {
    if (!peekInteractiveLoginSignal('remember-me-exposure')) {
      return
    }
    // A remembered session's step-up resume also marks the login signal (checkCode runs after the
    // 207 verify), but its enrollment already fired at the original fresh login — and only the
    // treatment arm can ever resume, so counting resumes would asymmetrically inflate that arm's
    // exposures. Consume the signal without firing. Gate on recognition provenance, NOT
    // status.persistent: this page's own mint flips persistent true mid-mount on a fresh login,
    // which would wrongly suppress genuine fresh-login exposures once LD readiness resolves.
    if (isRecognizedViaPersistentLogin || isTrustedCheckout) {
      consumeInteractiveLoginSignal('remember-me-exposure')
      return
    }
    if (isUndefinedOrEmpty(uuid) || ldClient === undefined) {
      return
    }
    // Until the client's context key matches this consumer AND LD reports ready, the variant could
    // be a stale (localStorage-bootstrapped or pre-login) value. Wait without consuming the signal;
    // the hasFeatureFlagsReady flip re-runs this effect once the app-level identify completes.
    const context = ldClient.getContext?.() as { key?: string } | undefined
    if (context?.key !== uuid || hasFeatureFlagsReady !== true) {
      return
    }
    // Do not fire while the variant is unresolved, when the experiment is inactive (LD targeting
    // OFF / errored / not found — the dark state produces zero events), or without a checkout
    // token, as the standard experiment-trigger path requires. The signal stays unconsumed.
    if (experiment.loading || !experiment.active || isEmptyCheckoutToken) {
      return
    }
    consumeInteractiveLoginSignal('remember-me-exposure')
    const { name, value, control } = experiment
    dispatch(triggerExperiment([name, value]))
    logEvent('Triggered Experiment', {
      experimentName: Experiments[name],
      experimentValue: value,
      experimentControl: control,
      experimentFlag: `${name}${value}`,
      exposureTrigger: 'login',
    })
  }, [uuid, hasFeatureFlagsReady, ldClient, isEmptyCheckoutToken, isRecognizedViaPersistentLogin, isTrustedCheckout, experiment, dispatch, logEvent])
}

// Fires the `Triggered Experiment` exposure for the inline Cash login experiment (CASHDAP-3181).
// Mounted on BOTH destination surfaces of the routing decision in `useCashAuthRoute` — the grant/QR
// page (`CashAuth`, control arm C) and the inline flow (`CashInlineAuth`, treatment arm A) — so the
// exposure is anchored to actually reaching a cash-auth surface, and both arms enroll.
//
// Gated on `useCashLoginType() === 'inline'`: only inline-capable consumers are bifurcated by the
// flag. `loginType === 'grant-flow'`/undefined users land on /cash-auth regardless of variant, so
// enrolling them would dilute the analysis population with users who cannot experience treatment.
//
// While the LD flag's targeting is OFF the evaluation reason is OFF → `experiment.active` is false
// (see `getExperiment`) and nothing fires — today's dark state produces zero events. Dispatches via
// `useTriggerExperimentEvent`, whose `activeExperiments` slice dedup fires once per session
// (whichever surface mounts first) and requires a non-empty checkout token. The ref guard bounds
// this mount to a single dispatch — `useTriggerExperimentEvent` returns a fresh closure every
// render, so without it the custom-properties object would re-run the effect indefinitely.
export const useTriggerCashInlineLoginExperimentExposure = (): void => {
  const experiment = useExperiment('CashInlineLogin', false)
  const loginType = useCashLoginType()
  const hasFeatureFlagsReady = useConsumerFeatureFlagReady()
  const triggerExperimentEvent = useTriggerExperimentEvent()
  const hasFiredRef = useRef(false)

  useEffect(() => {
    if (hasFiredRef.current) {
      return
    }
    if (loginType !== 'inline' || !hasFeatureFlagsReady || experiment.loading || !experiment.active) {
      return
    }
    hasFiredRef.current = true
    triggerExperimentEvent(experiment, { exposureTrigger: 'cash-auth' })
  }, [loginType, hasFeatureFlagsReady, experiment, triggerExperimentEvent])
}

export const useIsEligibleForAddPaymentMethodFlowExperiment = (): boolean => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const { value: addPaymentMethodFlowExperimentValue } = experiment
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isPaymentMethodEligible = useConsumerLendingEligible()
  const preferredCard = usePreferredCard()
  const cashAppPay = useActiveCashAppPay()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()

  // Mirrors the routing in LoggedIn.tsx — only users who would actually be sent down the
  // experimental no-payment-method branch should be enrolled, otherwise variant A and control
  // behave identically and dilute the analysis population. `selectPreferredCard` collapses the
  // full saved-card / fallback state into preferredCard.card: it's null only when the user has
  // no preferred and no usable fallback, and non-null in every other case (including the
  // errored-preferred-with-no-fallback edge), so this check matches LoggedIn's
  // `preferredCard?.card !== null` routing guard exactly.
  const hasNoUsablePaymentMethod = preferredCard?.card === null && cashAppPay.length === 0 && !shouldUseApplePay && !shouldUseBankAccount

  const eligibilityConditions = [
    addPaymentMethodFlowExperimentValue === 'A',
    cohort !== Cohort.CashOnly,
    !isBronteDesignLanguage,
    !isPaymentMethodEligible,
    hasNoUsablePaymentMethod,
  ]
  return eligibilityConditions.every((condition) => condition)
}

// Symmetric to useIsEligibleForAddPaymentMethodFlowExperiment but for the control arm. Returns
// true iff the user is a variant-C participant who would be routed to the standard
// add-payment-method page in LoggedIn.tsx. Same cohort/design/lending/no-payment-method gates
// as Test — the only difference is the variant check. Also requires `experiment.active` and
// !experiment.loading because `getExperiment` defaults `value` to 'C' while LD is loading
// (`apps/checkout/src/model/experiments.tsx`'s `loading` constant), and we must not enroll
// users whose variant assignment hasn't actually resolved yet.
//
// Background: Amplitude variant-C `Triggered Experiment` events dropped from ~30-60/day to 0
// on 2026-05-06 (the day PR #11859 deployed). The original eligibility hook only ever fires
// the trigger for variant=A users, so it can't have been the source of the pre-2026-05-06
// variant-C events — that means some other implicit mechanism was firing them and stopped.
// Restoring symmetric explicit firing here closes the gap without depending on whatever
// implicit mechanism existed before; the dispatcher's analyticsFired dedup in
// useTriggerExperimentEvent bounds any future double-firing to one event per session.
export const useIsControlForAddPaymentMethodFlowExperiment = (): boolean => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const { value, active, loading } = experiment
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isPaymentMethodEligible = useConsumerLendingEligible()
  const preferredCard = usePreferredCard()
  const cashAppPay = useActiveCashAppPay()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()

  const hasNoUsablePaymentMethod = preferredCard?.card === null && cashAppPay.length === 0 && !shouldUseApplePay && !shouldUseBankAccount

  return active && !loading && value === 'C' && cohort !== Cohort.CashOnly && !isBronteDesignLanguage && !isPaymentMethodEligible && hasNoUsablePaymentMethod
}

const usePi4SwitcherNoPaymentMethodExperimentEligibility = (
  selectedPaymentType?: PaymentType
): { eligible: boolean; experimentValue: ExperimentValue; experimentActive: boolean; experimentLoading: boolean } => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const { value: addPaymentMethodFlowExperimentValue, active, loading } = experiment
  const isPi4SwitcherSummaryFirstEnabled = useFlag(Features.CHECKOUT_ENABLE_ADD_PAYMENT_METHOD_PM_PI4_SWITCHER_SUMMARY_FIRST, false)
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const preferredCard = usePreferredCard()
  const cashAppPay = useActiveCashAppPay()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()

  const hasNoUsablePaymentMethod = preferredCard?.card === null && cashAppPay.length === 0 && !shouldUseApplePay && !shouldUseBankAccount

  const eligibilityConditions = [
    isPi4SwitcherSummaryFirstEnabled,
    selectedPaymentType === PaymentType.PBI,
    cohort !== Cohort.CashOnly,
    !isBronteDesignLanguage,
    isConsumerLendingEligible,
    hasNoUsablePaymentMethod,
  ]

  return {
    eligible: eligibilityConditions.every((condition) => condition),
    experimentValue: addPaymentMethodFlowExperimentValue,
    experimentActive: active,
    experimentLoading: loading,
  }
}

export const useShouldTriggerPi4SwitcherWithNoPaymentMethodExperiment = (selectedPaymentType?: PaymentType): boolean => {
  const { eligible, experimentValue, experimentActive, experimentLoading } = usePi4SwitcherNoPaymentMethodExperimentEligibility(selectedPaymentType)

  return eligible && experimentActive && !experimentLoading && (experimentValue === 'A' || experimentValue === 'C')
}

export const useShouldRoutePi4SwitcherWithNoPaymentMethodToSummary = (selectedPaymentType?: PaymentType): boolean => {
  const { eligible, experimentValue, experimentActive, experimentLoading } = usePi4SwitcherNoPaymentMethodExperimentEligibility(selectedPaymentType)

  return eligible && experimentActive && !experimentLoading && experimentValue === 'A'
}

// Returns a callback that fires the Triggered Experiment event for the current add-payment-method
// experiment assignment. Caller is responsible for invoking this only when the user is actually
// routed through a no-payment-method branch (currently from LoggedIn.tsx, gated on requestCompleted +
// hasFeatureFlagsReady). The eligibility hooks above intentionally do NOT auto-fire because
// `preferredCard?.card === null` can be transiently true (e.g. after `resetPreferredPaymentMethodErrors`
// mutates the card field, or before `setCardsForExpiryLimit` runs with a populated `started` payload),
// which previously enrolled users with saved cards and diluted the variant-A pool.
//
// Dispatches synchronously rather than using the `useExperimentTrigger` setState+useEffect
// indirection: callers invoke this immediately before `router.replace`, so the LoggedIn page
// can unmount in the next render and skip a deferred effect. Calling `useTriggerExperimentEvent`
// directly guarantees the Redux dispatch + analytics logEvent happen in the same execution as
// the trigger() invocation.
export const useAddPaymentMethodFlowExperimentTrigger = (): ((customEventProperties?: Record<string, unknown>) => void) => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const triggerExperimentEvent = useTriggerExperimentEvent()
  return (customEventProperties) => {
    if (experiment.active && !experiment.loading) {
      triggerExperimentEvent(experiment, customEventProperties)
    }
  }
}
